import {useCallback, useLayoutEffect, useRef, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Switch,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {AnimatePresence, LayoutGroup, motion} from 'framer-motion';
import {
  TablerBolt,
  TablerCalendar,
  TablerChevronDown,
  TablerMapPin,
  TablerPhone,
  TablerPlayerPlay,
  TablerVideo,
  TablerX,
} from '../../icons';
import usePlannerStyles, {useScrollbarSx} from './usePlannerStyles';
import {
  PlannerList,
  plannerLanguageMarker,
  PLANNER_OPALITE_SELECTION_RADIUS,
} from './PlannerSidebarList';
import {plannerSearchHighlightNode} from './PlannerSearchHighlight';
import {patientItemToPatientRow} from './plannerBrowserRowAdapters';
import {
  combinePhoneExtension,
  formatCreateSessionPhoneNumber,
  patientDisplayName,
  patientLanguage,
  patientMetadata,
  splitPhoneExtension,
} from './createSessionFormUtils';
import {CREATE_SESSION_SECTION_LABEL_PROPS} from './createSessionSectionLayout';
import ManualCallModal from '../common/ManualCallModal';
import CallerIdField from './CallerIdField';
import FavoriteStar from '../common/FavoriteStar';
import MetaPill from '../common/MetaPill';
import useLanguageFavorites from '../../hooks/useLanguageFavorites';
import {partitionByFavorite} from '../../common/languageFavoritesOrder';

export const CREATE_SESSION_CARD_PADDING = {base: 4, md: 5};

const LOCATION_OPTIONS = [
  {id: 'inPerson', label: 'In-person', icon: TablerMapPin},
  {id: 'phone', label: 'Phone', icon: TablerPhone},
  {id: 'video', label: 'Video', icon: TablerVideo},
];

const INTERPRETER_OPTIONS = [
  {id: 'no', label: 'No', enabled: false},
  {id: 'yes', label: 'Yes', enabled: true},
];

const visuallyHiddenInputStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
};

// The single inline clear affordance for typeahead inputs.
const InputClearButton = ({label, onClick}: Record<string, any>) => (
  <IconButton
    aria-label={label}
    icon={<TablerX size={14} aria-hidden="true" />}
    size="xs"
    variant="ghost"
    borderRadius="full"
    position="absolute"
    top="10px"
    right="36px"
    zIndex={2}
    onClick={onClick}
  />
);

const DROPDOWN_OPTION_SELECTOR = '[role="option"]';
const DROPDOWN_BOTTOM_GAP_EM = 0.5;
const DROPDOWN_FALLBACK_FONT_SIZE_PX = 16;
// One shared easing curve and a small set of durations drive every form
// reveal so opening, closing, swapping modalities, and nested fields all feel
// like the same motion system rather than a grab-bag of one-off transitions.
const FORM_MOTION_EASE = [0.22, 1, 0.36, 1];
const LANGUAGE_DIALECT_MOVE_DURATION_MS = 200;
const LANGUAGE_DIALECT_MOVE_EASE_CSS = 'cubic-bezier(0.22, 1, 0.36, 1)';
const LANGUAGE_DIALECT_MOVE_EASE = FORM_MOTION_EASE;
const FORM_HEIGHT_TRANSITION = {
  duration: 0.26,
  ease: FORM_MOTION_EASE,
};
const FORM_FADE_TRANSITION = {
  duration: 0.16,
  ease: FORM_MOTION_EASE,
};
const FORM_COLLAPSE_TRANSITION = {
  height: FORM_HEIGHT_TRANSITION,
  opacity: FORM_FADE_TRANSITION,
};
const DIALECT_PICKER_FADE_TRANSITION = {
  duration: 0.14,
  ease: LANGUAGE_DIALECT_MOVE_EASE,
  delay: 0,
};

const MotionBox = motion(Box);

// Border-box height of the natural content, deliberately ignoring absolutely
// positioned descendants (open dropdowns) so a popover never re-triggers the
// height animation or gets clipped.
const measureNaturalHeight = (node: HTMLElement | null) =>
  node ? node.getBoundingClientRect().height || node.scrollHeight || 0 : 0;

const heightsDiffer = (a: number, b: number) => Math.abs(a - b) >= 0.5;

// A height-aware reveal that keeps its animated height locked to the natural
// height of whatever it is wrapping. Because every size change — first open,
// final collapse, a modality swap that changes the body, or a nested field
// appearing — flows through the same animated height, they all glide on one
// consistent curve instead of snapping. `contentKey` lets callers cross-fade
// the body when the content itself swaps (e.g. phone ↔ video).
const CollapsibleContent = ({
  children,
  isVisible,
  marginTop,
  mt,
  contentKey,
  ...rest
}: Record<string, any>) => {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const lastVisibleChildrenRef = useRef(children);
  const lastContentKeyRef = useRef(contentKey);
  const [isMounted, setIsMounted] = useState(Boolean(isVisible));
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const topSpacing = mt ?? marginTop;

  // Freeze the most recent open content + key so a collapse animates out what
  // was actually showing, even after the parent has already swapped to an
  // empty/in-person body.
  if (isVisible) {
    lastVisibleChildrenRef.current = children;
    lastContentKeyRef.current = contentKey;
  }

  const setOverflow = useCallback((value: string) => {
    if (outerRef.current) {
      outerRef.current.style.overflow = value;
    }
  }, []);

  const syncMeasuredHeight = useCallback(() => {
    const next = measureNaturalHeight(innerRef.current);
    setMeasuredHeight(prev => (heightsDiffer(prev, next) ? next : prev));
  }, []);

  // Mount as soon as we become visible; unmounting waits for the collapse to
  // finish (see onAnimationComplete) so the closing body animates out.
  useLayoutEffect(() => {
    if (isVisible) setIsMounted(true);
  }, [isVisible]);

  // Re-measure on every commit so swaps and nested reveals immediately retarget
  // the height before paint (no one-frame overlap with the siblings below).
  useLayoutEffect(() => {
    if (isMounted) syncMeasuredHeight();
  });

  // Drop focus from controls that are about to collapse away.
  useLayoutEffect(() => {
    if (isVisible || typeof document === 'undefined') return;
    const active = document.activeElement as HTMLElement | null;
    if (active && innerRef.current?.contains(active)) {
      active.blur?.();
    }
  }, [isVisible]);

  // Catch async size changes (fonts, responsive reflow, late content) too.
  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(syncMeasuredHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMounted, syncMeasuredHeight]);

  if (!isMounted) return null;

  const renderedChildren = isVisible
    ? children
    : lastVisibleChildrenRef.current;
  const activeContentKey = isVisible ? contentKey : lastContentKeyRef.current;
  const innerContent =
    topSpacing == null ? (
      renderedChildren
    ) : (
      <Box pt={topSpacing}>{renderedChildren}</Box>
    );

  return (
    <MotionBox
      ref={outerRef}
      position="relative"
      overflow="hidden"
      initial={{height: 0, opacity: 0}}
      animate={{
        height: isVisible ? measuredHeight : 0,
        opacity: isVisible ? 1 : 0,
      }}
      transition={FORM_COLLAPSE_TRANSITION}
      onAnimationStart={() => setOverflow('hidden')}
      onUpdate={(latest: Record<string, any>) => {
        // Keep the body clipped until the animated height actually reaches the
        // content height. Without this, an early opacity-only pass (which runs
        // before the height has been measured on open) would expose the full
        // body while the box is still expanding, reading as an instant pop.
        if (!isVisible) return;
        const current =
          typeof latest.height === 'number' ? latest.height : measuredHeight;
        setOverflow(
          heightsDiffer(current, measuredHeight) ? 'hidden' : 'visible',
        );
      }}
      onAnimationComplete={() => {
        if (!isVisible) {
          setIsMounted(false);
          return;
        }
        // Final guarantee: only let absolutely positioned popovers
        // (language/dialect menus) spill out once we've truly settled at the
        // resting height.
        const current = outerRef.current
          ? outerRef.current.getBoundingClientRect().height
          : measuredHeight;
        if (!heightsDiffer(current, measuredHeight)) {
          setOverflow('visible');
        }
      }}
      {...rest}
    >
      <Box ref={innerRef}>
        <MotionBox
          key={activeContentKey ?? 'content'}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={FORM_FADE_TRANSITION}
        >
          {innerContent}
        </MotionBox>
      </Box>
    </MotionBox>
  );
};

const dropdownOptions = (menu: Element | null) =>
  Array.from(menu?.querySelectorAll(DROPDOWN_OPTION_SELECTOR) || []).filter(
    (node): node is HTMLElement =>
      node instanceof HTMLElement &&
      !node.hasAttribute('disabled') &&
      node.getAttribute('aria-disabled') !== 'true',
  );

const focusDropdownOption = (
  menuId: any,
  direction: 'first' | 'last' | 'next' | 'previous',
) => {
  const menu = document.getElementById(String(menuId || ''));
  const options = dropdownOptions(menu);
  if (!options.length) return;

  const activeIndex = options.indexOf(document.activeElement as HTMLElement);
  let nextIndex = 0;
  if (direction === 'last') nextIndex = options.length - 1;
  if (direction === 'next')
    nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % options.length;
  if (direction === 'previous')
    nextIndex =
      activeIndex < 0
        ? options.length - 1
        : (activeIndex - 1 + options.length) % options.length;

  options[nextIndex]?.focus();
  options[nextIndex]?.scrollIntoView?.({block: 'nearest'});
};

const focusDropdownOptionSoon = (menuId: any, direction: 'first' | 'last') => {
  window.setTimeout(() => focusDropdownOption(menuId, direction), 0);
};

export const handleDropdownInputKeyDown = ({
  event,
  menuId,
  onOpen,
}: Record<string, any>) => {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
  event.preventDefault();
  event.stopPropagation();
  onOpen?.();
  focusDropdownOptionSoon(menuId, event.key === 'ArrowDown' ? 'first' : 'last');
};

export const handleDropdownOptionKeyDown = (
  event: any,
  inputSelector: string,
) => {
  if (
    event.key !== 'ArrowDown' &&
    event.key !== 'ArrowUp' &&
    event.key !== 'Escape'
  )
    return;
  event.preventDefault();
  event.stopPropagation();

  if (event.key === 'Escape') {
    const menu = event.currentTarget?.closest?.('[role="listbox"]');
    const input =
      menu?.parentElement?.querySelector?.(inputSelector) ||
      (typeof document !== 'undefined'
        ? document.querySelector(inputSelector)
        : null);
    input?.focus?.();
    return;
  }

  const menuId = event.currentTarget?.closest?.('[role="listbox"]')?.id;
  focusDropdownOption(menuId, event.key === 'ArrowDown' ? 'next' : 'previous');
};

export const ViewportAwareDropdownMenu = ({
  anchorRef = null,
  children,
  gapPx = 6,
  maxHeightPx = 260,
  position,
  top,
  left,
  right,
  width,
  ...rest
}: Record<string, any>) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Every typeahead menu built on this primitive gets the planner's
  // always-visible scrollbar: dropdown lists are exactly where a hidden
  // overlay scrollbar strands users on twitchy mice.
  const dropdownScrollbarSx = useScrollbarSx();
  // Measure the anchor and decide whether the menu should open below it (the
  // default) or flip above it. Flipping is what keeps the menu from collapsing
  // into a tiny scroll area when its field sits low in a modal — e.g. the
  // "Languages you speak" picker, which is the last field in the onboarding
  // modal. Placement and max-height are computed together so they never drift.
  const measurePlacement = useCallback(() => {
    const rect = anchorRef?.current?.getBoundingClientRect?.();
    if (!rect || typeof window === 'undefined') return null;
    const fontSizePx = menuRef.current
      ? Number.parseFloat(window.getComputedStyle(menuRef.current).fontSize) ||
        DROPDOWN_FALLBACK_FONT_SIZE_PX
      : DROPDOWN_FALLBACK_FONT_SIZE_PX;
    const viewportGapPx = fontSizePx * DROPDOWN_BOTTOM_GAP_EM;
    const spaceBelow = Math.max(
      0,
      Math.floor(window.innerHeight - (rect.bottom + gapPx) - viewportGapPx),
    );
    const spaceAbove = Math.max(
      0,
      Math.floor(rect.top - gapPx - viewportGapPx),
    );
    // Prefer opening downward; flip up only when the menu can't show a useful
    // amount below but has more room above.
    const flipUp = spaceBelow < Math.min(maxHeightPx, spaceAbove);
    const available = flipUp ? spaceAbove : spaceBelow;
    const base = {
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      visibility: 'visible',
      maxHeight: `${Math.min(maxHeightPx, available)}px`,
      placement: flipUp ? 'above' : 'below',
    };
    return flipUp
      ? {
          ...base,
          top: 'auto',
          bottom: `${Math.max(0, window.innerHeight - rect.top + gapPx)}px`,
        }
      : {...base, top: `${rect.bottom + gapPx}px`, bottom: 'auto'};
  }, [anchorRef, gapPx, maxHeightPx]);
  const [placement, setPlacement] = useState<any>(measurePlacement);

  useLayoutEffect(() => {
    if (!anchorRef || typeof window === 'undefined') return undefined;

    let frameId: number | null = null;
    const updatePlacement = () => {
      setPlacement(measurePlacement());
    };
    const schedulePlacementUpdate = () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updatePlacement);
    };

    // Measure synchronously, then again on the next frame. When the menu and
    // its anchor mount in the same commit, the parent anchor's ref hasn't been
    // attached yet while this child effect runs, so the synchronous read sees
    // `null`. The deferred re-measure runs once the ref is attached (and once
    // the menu's own box exists, giving an accurate font size), snapping the
    // menu to the anchor instead of leaving it at the fallback position.
    updatePlacement();
    schedulePlacementUpdate();
    window.addEventListener('resize', schedulePlacementUpdate);
    window.addEventListener('scroll', schedulePlacementUpdate, true);

    return () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', schedulePlacementUpdate);
      window.removeEventListener('scroll', schedulePlacementUpdate, true);
    };
  }, [anchorRef, measurePlacement]);

  const resolvedPlacement = anchorRef
    ? placement || {
        // Until the anchor is measured (e.g. the anchor's ref hasn't attached
        // yet on the first commit), keep the menu hidden rather than flashing
        // it at the viewport's top-left corner. The deferred re-measure in the
        // layout effect promotes it to the anchored position once available.
        position: 'fixed',
        top: '0px',
        left: '0px',
        width: '100vw',
        visibility: 'hidden',
        maxHeight: `${maxHeightPx}px`,
        placement: 'below',
      }
    : {
        position,
        top,
        left,
        right,
        width,
        maxHeight: `${maxHeightPx}px`,
        placement: 'below',
      };

  const {
    maxHeight,
    placement: resolvedDirection,
    ...placementProps
  } = resolvedPlacement;

  const menuNode = (
    <Box
      ref={menuRef}
      boxSizing="border-box"
      maxH={maxHeight}
      overflowY="auto"
      sx={dropdownScrollbarSx}
      data-viewport-bottom-gap-em={DROPDOWN_BOTTOM_GAP_EM}
      data-viewport-max-height={maxHeight}
      data-viewport-placement={resolvedDirection}
      data-portaled={anchorRef ? 'true' : 'false'}
      {...rest}
      {...placementProps}
    >
      {children}
    </Box>
  );

  return anchorRef ? <Portal>{menuNode}</Portal> : menuNode;
};

export const SearchSelectionInput = ({
  ariaControls,
  ariaLabel,
  autoFocus = false,
  isOpen = false,
  needsAttention = false,
  onChange,
  onClick,
  onFocus,
  onKeyDown,
  placeholder,
  value,
}: Record<string, any>) => {
  const {borderColor, inputFocusStyles, subtleText} = usePlannerStyles();
  const attentionBorderColor = useColorModeValue('red.400', 'red.300');
  const resolvedBorderColor = needsAttention
    ? attentionBorderColor
    : borderColor;

  return (
    <InputGroup
      className="ui fluid search selection dropdown"
      data-semantic-ui-definition="search-selection"
      w="100%"
    >
      <Input
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onClick={onClick}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? ariaControls : undefined}
        borderRadius="lg"
        borderColor={resolvedBorderColor}
        borderStyle={needsAttention ? 'dashed' : 'solid'}
        h="44px"
        minH="44px"
        pr="4.75rem"
        _hover={{borderColor: resolvedBorderColor}}
        _focusVisible={inputFocusStyles}
      />
      <InputRightElement pointerEvents="none" color={subtleText} h="44px">
        <TablerChevronDown size={16} aria-hidden="true" />
      </InputRightElement>
    </InputGroup>
  );
};

const SegmentedRadioOption = ({
  name,
  value,
  label,
  ariaLabel,
  icon: Icon,
  isSelected,
  onChange,
  minH = '44px',
  px = 3,
  noTruncate = false,
}: Record<string, any>) => {
  const {
    focusShadow,
    hoverBg,
    selectedSegmentBg,
    selectedSegmentBorderColor,
    selectedSegmentColor,
    selectedSegmentShadow,
    subtleText,
    textColor,
    controlFontSize,
  } = usePlannerStyles();

  return (
    <Box
      as="label"
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap={2}
      flex="1"
      minW={noTruncate ? 'max-content' : undefined}
      minH={minH}
      px={px}
      borderRadius="md"
      borderWidth="1px"
      borderColor={isSelected ? selectedSegmentBorderColor : 'transparent'}
      bg={isSelected ? selectedSegmentBg : 'transparent'}
      color={isSelected ? selectedSegmentColor : subtleText}
      boxShadow={isSelected ? selectedSegmentShadow : 'none'}
      fontSize={controlFontSize}
      fontWeight="normal"
      cursor="pointer"
      transition="background 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease"
      _hover={{
        bg: isSelected ? selectedSegmentBg : hoverBg,
        color: isSelected ? selectedSegmentColor : textColor,
      }}
      _focusWithin={{
        boxShadow: focusShadow,
      }}
    >
      <Box
        as="input"
        type="radio"
        name={name}
        value={value}
        checked={isSelected}
        onChange={onChange}
        aria-label={ariaLabel || label}
        sx={visuallyHiddenInputStyles}
      />
      {Icon && <Icon size={15} aria-hidden="true" focusable="false" />}
      <Text
        as="span"
        noOfLines={noTruncate ? undefined : 1}
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    </Box>
  );
};

const patientDropdownRowKey = (item: any, name: string, index: any) =>
  item?.id ||
  item?.localPatient?.id ||
  item?.ehrPatient?.id ||
  item?.patient?.id ||
  `${name}-${index}`;

const patientDropdownMetadata = (item: any, metadataFormatter: any) =>
  String(metadataFormatter(item) || '').replace(/\s+·\s+/g, ' • ');

const patientItemToDropdownRow = ({
  handleSelectPatient,
  index = 0,
  item,
  metadataFormatter,
  patientSearch,
}: Record<string, any>) => {
  const name = patientDisplayName(item) || 'Unnamed patient';
  const metadata = patientDropdownMetadata(item, metadataFormatter);
  const key = patientDropdownRowKey(item, name, index);
  const nameNode = plannerSearchHighlightNode(name, patientSearch);
  const contextNode = plannerSearchHighlightNode(metadata, patientSearch);
  const row = patientItemToPatientRow(item, {
    index,
    onSelectPatient: handleSelectPatient
      ? () => handleSelectPatient(item)
      : undefined,
    searchQuery: patientSearch,
    source: 'dropdown',
  });

  if (!row) return null;
  return {
    key,
    cardKind: 'dropdown',
    timestamp: row.timestamp,
    time: row.time,
    name,
    nameNode,
    language: patientLanguage(item),
    contextText: metadata,
    contextNode,
    ariaLabel: handleSelectPatient ? `Select ${name}` : row.ariaLabel,
    isSelected: row.isSelected,
    onSelect: row.onSelect,
    rowProps: {
      ...(row.rowProps || {}),
      className: 'item',
      role: handleSelectPatient ? 'option' : undefined,
      'aria-selected': handleSelectPatient ? false : undefined,
      'data-planner-patient-index': index,
      'data-planner-patient-anchor-id': `create-session-${key}`,
      onMouseDown: handleSelectPatient
        ? (event: any) => event.preventDefault()
        : undefined,
      onKeyDown: handleSelectPatient
        ? (event: any) =>
            handleDropdownOptionKeyDown(
              event,
              'input[aria-label="Select patient"]',
            )
        : undefined,
    },
  };
};

const buildPatientDropdownRows = ({
  filteredPatients,
  handleSelectPatient,
  metadataFormatter,
  patientSearch,
}: Record<string, any>) =>
  (filteredPatients || [])
    .map((item: any, index: any) =>
      patientItemToDropdownRow({
        handleSelectPatient,
        index,
        item,
        metadataFormatter,
        patientSearch,
      }),
    )
    .filter(Boolean);

export const PatientSection = ({
  autoFocusPatientSearch = false,
  compact = false,
  filteredPatients,
  handleClearPatient,
  handleEditPatient,
  handlePatientSearchChange,
  handleSelectPatient,
  isDeepSearching,
  isLoadingPatients,
  isSearchingEhrPatients,
  onPatientSearchFocus,
  patientSearch,
  patientTypeaheadRef,
  selectedPatientItem,
  selectedPatientName,
  shouldShowResults,
  visibleErrors = {},
  metadataFormatter = patientMetadata,
  headingLabel = 'Patient',
  headingTextTransform = compact ? 'uppercase' : undefined,
  headingLetterSpacing = compact ? '0.08em' : undefined,
  showHeading = true,
  portalResults = true,
}: Record<string, any>) => {
  const plannerStyles = usePlannerStyles();
  const {borderColor, subtleText, surfaceBg, textColor} = plannerStyles;
  const isPatientLookupBusy = Boolean(
    isLoadingPatients || isSearchingEhrPatients || isDeepSearching,
  );
  const hasPatientQuery = String(patientSearch || '').trim().length > 0;
  // Before the user types anything, an empty roster shouldn't read as a dead
  // end — prompt them to search instead of declaring "No patients found".
  const patientDropdownEmptyText = isPatientLookupBusy
    ? 'Loading patients...'
    : hasPatientQuery
      ? 'No patients found'
      : 'Start typing to search';
  const patientDropdownRows = buildPatientDropdownRows({
    filteredPatients,
    handleSelectPatient,
    metadataFormatter,
    patientSearch,
  });
  const selectedPatientLabel =
    selectedPatientName ||
    patientDisplayName(selectedPatientItem) ||
    'Selected patient';
  const selectedPatientLanguage = patientLanguage(selectedPatientItem);
  const selectedPatientMarker = plannerLanguageMarker(selectedPatientLanguage);
  const selectedPatientRows = selectedPatientItem
    ? [
        {
          ...patientItemToDropdownRow({
            handleSelectPatient: null,
            index: 0,
            item: selectedPatientItem,
            metadataFormatter,
            patientSearch: '',
          }),
          key: 'selected-patient',
          cardKind: 'dropdown',
          name: selectedPatientLabel,
          nameNode: (
            <>
              {selectedPatientLabel}
              {selectedPatientMarker && (
                <Box
                  as="span"
                  aria-hidden="true"
                  display="inline-flex"
                  ml={2}
                  verticalAlign="-0.08em"
                >
                  {selectedPatientMarker}
                </Box>
              )}
            </>
          ),
          language: '',
          ariaLabel: `Selected patient ${selectedPatientLabel}`.trim(),
          rowProps: {
            'data-testid': 'selected-patient',
          },
        },
      ].filter(Boolean)
    : [];

  return (
    <Box as="section" aria-labelledby="create-session-patient-heading" w="100%">
      <Flex
        gap={{base: 2, md: 3}}
        align={{base: 'stretch', md: 'flex-start'}}
        direction={compact ? 'column' : {base: 'column', md: 'row'}}
        w="100%"
      >
        {showHeading ? (
          <Text
            as="h3"
            id="create-session-patient-heading"
            color={textColor}
            fontWeight="normal"
            fontSize={compact ? 'xs' : 'md'}
            display={compact ? 'block' : {md: 'flex'}}
            alignItems={{md: 'center'}}
            minH={compact ? undefined : {md: '44px'}}
            minW={compact ? undefined : {md: '132px'}}
            textTransform={headingTextTransform}
            letterSpacing={headingLetterSpacing}
          >
            {headingLabel}
          </Text>
        ) : null}
        <Box flex="1" minW={0} w="100%">
          {selectedPatientItem ? (
            <Box
              data-testid="selected-patient-card"
              display="grid"
              gridTemplateColumns="minmax(0, 1fr) 44px"
              minW={0}
              bg={surfaceBg}
              borderWidth="1px"
              borderColor={plannerStyles.listRowDividerColor || borderColor}
              borderRadius={PLANNER_OPALITE_SELECTION_RADIUS}
              overflow="hidden"
            >
              <Box
                minW={0}
                role={handleEditPatient ? 'button' : undefined}
                tabIndex={handleEditPatient ? 0 : undefined}
                aria-label={
                  handleEditPatient ? 'Change selected patient' : undefined
                }
                cursor={handleEditPatient ? 'pointer' : undefined}
                onClick={handleEditPatient}
                onKeyDown={
                  handleEditPatient
                    ? (event: any) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleEditPatient();
                        }
                      }
                    : undefined
                }
                transition="background-color 120ms ease"
                _hover={
                  handleEditPatient
                    ? {bg: plannerStyles.listRowSelectedBg}
                    : undefined
                }
                _focusVisible={
                  handleEditPatient
                    ? plannerStyles.focusOutlineStyles
                    : undefined
                }
              >
                <PlannerList
                  rows={selectedPatientRows}
                  styles={plannerStyles}
                  progressiveKey="create-session-selected-patient"
                  dateGrouping="none"
                  showTimeColumn={false}
                  progressive={false}
                />
              </Box>
              <Box
                minW={0}
                minH="44px"
                display="flex"
                alignItems="stretch"
                justifyContent="stretch"
              >
                <IconButton
                  data-testid="selected-patient-clear"
                  aria-label="Clear selected patient"
                  type="button"
                  icon={<TablerX size={16} aria-hidden="true" />}
                  variant="unstyled"
                  borderRadius="none"
                  borderWidth={0}
                  boxShadow="none"
                  w="100%"
                  h="100%"
                  minH="44px"
                  color={subtleText}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  onClick={handleClearPatient}
                  _hover={{bg: 'transparent'}}
                  _active={{bg: 'transparent'}}
                  _focusVisible={plannerStyles.focusOutlineStyles}
                />
              </Box>
            </Box>
          ) : (
            <FormControl isInvalid={Boolean(visibleErrors.patient)}>
              <Box
                ref={patientTypeaheadRef}
                data-testid="create-session-patient-typeahead"
                flex="1"
                minW={0}
                position="relative"
                w="100%"
              >
                <SearchSelectionInput
                  ariaControls="create-session-patient-results"
                  ariaLabel="Select patient"
                  autoFocus={autoFocusPatientSearch}
                  isOpen={shouldShowResults}
                  onChange={handlePatientSearchChange}
                  onClick={onPatientSearchFocus}
                  onFocus={onPatientSearchFocus}
                  onKeyDown={(event: any) =>
                    handleDropdownInputKeyDown({
                      event,
                      menuId: 'create-session-patient-results',
                      onOpen: onPatientSearchFocus,
                    })
                  }
                  placeholder="Select patient"
                  value={patientSearch}
                />

                {shouldShowResults && (
                  <ViewportAwareDropdownMenu
                    anchorRef={portalResults ? patientTypeaheadRef : null}
                    id="create-session-patient-results"
                    className="menu"
                    position={portalResults ? undefined : 'absolute'}
                    top={portalResults ? undefined : 'calc(100% + 6px)'}
                    left={portalResults ? undefined : 0}
                    right={portalResults ? undefined : 0}
                    zIndex={portalResults ? 1400 : 6}
                    bg={surfaceBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                    boxShadow="lg"
                    maxHeightPx={260}
                    role="listbox"
                  >
                    <PlannerList
                      rows={patientDropdownRows}
                      emptyText={patientDropdownEmptyText}
                      styles={plannerStyles}
                      progressiveKey="create-session-patient-dropdown"
                      dateGrouping="none"
                      showTimeColumn={false}
                      progressive={false}
                    />
                  </ViewportAwareDropdownMenu>
                )}
              </Box>
              <FormErrorMessage>{visibleErrors.patient}</FormErrorMessage>
            </FormControl>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export const InterpreterSection = ({
  dialect,
  dialectOptions,
  dialectSearch,
  dialectTypeaheadRef,
  filteredDialectOptions,
  filteredLanguageOptions,
  handleClearDialect,
  handleClearLanguage,
  handleDialectSearchChange,
  handleInterpreterToggle,
  handleLanguageFocus,
  handleLanguageSearchChange,
  handleSelectDialectOption,
  handleSelectLanguageOption,
  interpreterEnabled,
  simultaneousEnabled = false,
  setSimultaneousEnabled,
  showSimultaneous = false,
  isDialectTypeaheadOpen,
  isLanguageTypeaheadOpen,
  language,
  languageSearch,
  languageTypeaheadRef,
  setIsDialectTypeaheadOpen,
  setIsLanguageTypeaheadOpen,
  showTopDivider = false,
  shouldShowDialectTypeahead,
  shouldShowLanguageSearchStatus,
  visibleErrors,
}: Record<string, any>) => {
  const {
    accentColor,
    borderColor,
    commonBadgeBg,
    commonBadgeColor,
    subtleText,
    surfaceBg,
    textColor,
    trackBg,
    typeaheadHoverBg,
    typeaheadSelectedBg,
    typeaheadSelectedBorder,
  } = usePlannerStyles();
  const {favoriteSet, isFavorite, toggleFavorite} = useLanguageFavorites();
  // Auto-detect is pinned above every group and never partitioned into
  // Favorites / All languages (it has no flag, star, or dialects).
  const autoDetectOption =
    filteredLanguageOptions.find((option: any) => option.isAutoDetect) || null;
  const standardLanguageOptions = filteredLanguageOptions.filter(
    (option: any) => !option.isAutoDetect,
  );
  const {favorites: favoriteLanguageOptions, others: otherLanguageOptions} =
    partitionByFavorite(
      standardLanguageOptions,
      (option: any) => option.language,
      favoriteSet,
    );
  const hasDialectPicker = Boolean(
    shouldShowDialectTypeahead && dialectOptions.length > 0,
  );

  // One row renderer shared by the favorites group and the rest, so a starred
  // language looks identical wherever it sits. The MotionBox `layout` wrapper is
  // what slides a row up into (or back out of) the favorites group when its
  // star is toggled.
  const renderLanguageOption = (option: any) => {
    const languageMarker = plannerLanguageMarker(option.language);
    const optionIsFavorite = isFavorite(option.language);
    return (
      <MotionBox key={option.id} layout w="100%">
        <Button
          type="button"
          className="item"
          role="option"
          aria-selected={language === option.language}
          aria-label={`Select ${option.label}`}
          variant="ghost"
          justifyContent="space-between"
          w="100%"
          h="auto"
          minH="48px"
          py={2.5}
          px={3}
          borderRadius="lg"
          bg={
            language === option.language ? typeaheadSelectedBg : 'transparent'
          }
          borderWidth="1px"
          borderColor={
            language === option.language
              ? typeaheadSelectedBorder
              : 'transparent'
          }
          // Safari does not focus a button on mousedown, so the input blurs and
          // the surrounding onBlur closes this menu before the click lands.
          // Preventing the mousedown default keeps focus on the input so the
          // selection registers.
          onMouseDown={event => event.preventDefault()}
          onClick={() => handleSelectLanguageOption(option)}
          onKeyDown={event =>
            handleDropdownOptionKeyDown(event, 'input[aria-label="Language"]')
          }
          _hover={{
            bg:
              language === option.language
                ? typeaheadSelectedBg
                : typeaheadHoverBg,
          }}
          _focus={{
            bg:
              language === option.language
                ? typeaheadSelectedBg
                : typeaheadHoverBg,
            borderColor:
              language === option.language
                ? typeaheadSelectedBorder
                : 'transparent',
            boxShadow: 'none',
            outline: 'none',
          }}
          _focusVisible={{
            bg:
              language === option.language
                ? typeaheadSelectedBg
                : typeaheadHoverBg,
            borderColor:
              language === option.language
                ? typeaheadSelectedBorder
                : 'transparent',
            boxShadow: 'none',
            outline: 'none',
          }}
        >
          <HStack
            spacing={2.5}
            minW={0}
            flex="1 1 auto"
            justify="flex-start"
            overflow="hidden"
          >
            <Text
              as="span"
              aria-hidden="true"
              color={textColor}
              fontSize="md"
              lineHeight="1"
              minW="1.625rem"
              textAlign="center"
            >
              {languageMarker || '\u00A0'}
            </Text>
            <Text
              color={textColor}
              fontWeight="normal"
              fontSize="md"
              noOfLines={1}
              minW={0}
            >
              {option.label}
            </Text>
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            {option.dialectCount > 0 && (
              <Text fontSize="sm" color={subtleText}>
                {option.dialectCount} dialect
                {option.dialectCount !== 1 ? 's' : ''}
              </Text>
            )}
            {option.isCommon && (
              <Text
                fontSize="sm"
                bg={commonBadgeBg}
                color={commonBadgeColor}
                px={2}
                py={0.5}
                borderRadius="full"
                fontWeight="normal"
              >
                Common
              </Text>
            )}
            <FavoriteStar
              isFavorite={optionIsFavorite}
              onToggle={() => toggleFavorite(option.language)}
              language={option.label}
            />
          </HStack>
        </Button>
      </MotionBox>
    );
  };

  // Pinned Auto-detect row: bolt icon + accent color instead of a flag, no
  // dialect count, no favorite star. Selecting it sends the 'auto' wire value
  // and the interpreter identifies the patient's language from their speech.
  const renderAutoDetectOption = (option: any) => {
    const isSelected = language === option.language;
    return (
      <MotionBox key={option.id} layout w="100%">
        <Button
          type="button"
          className="item"
          role="option"
          aria-selected={isSelected}
          aria-label="Select Auto-detect language"
          data-testid="language-option-auto-detect"
          variant="ghost"
          justifyContent="space-between"
          w="100%"
          h="auto"
          minH="48px"
          py={2.5}
          px={3}
          borderRadius="lg"
          bg={isSelected ? typeaheadSelectedBg : 'transparent'}
          borderWidth="1px"
          borderColor={isSelected ? typeaheadSelectedBorder : 'transparent'}
          onMouseDown={event => event.preventDefault()}
          onClick={() => handleSelectLanguageOption(option)}
          onKeyDown={event =>
            handleDropdownOptionKeyDown(event, 'input[aria-label="Language"]')
          }
          _hover={{bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg}}
          _focus={{
            bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg,
            borderColor: isSelected ? typeaheadSelectedBorder : 'transparent',
            boxShadow: 'none',
            outline: 'none',
          }}
          _focusVisible={{
            bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg,
            borderColor: isSelected ? typeaheadSelectedBorder : 'transparent',
            boxShadow: 'none',
            outline: 'none',
          }}
        >
          <HStack
            spacing={2.5}
            minW={0}
            flex="1 1 auto"
            justify="flex-start"
            overflow="hidden"
          >
            <Box
              as="span"
              aria-hidden="true"
              minW="1.625rem"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={TablerBolt} color={accentColor} boxSize={4} />
            </Box>
            <Text
              color={accentColor}
              fontWeight="medium"
              fontSize="md"
              noOfLines={1}
              minW={0}
            >
              {option.label}
            </Text>
          </HStack>
          <Text fontSize="sm" color={subtleText} flexShrink={0}>
            Identifies from speech
          </Text>
        </Button>
      </MotionBox>
    );
  };

  const renderLanguageGroupLabel = (label: string, key: string) => (
    <MotionBox key={key} layout>
      <Text
        fontSize="xs"
        fontWeight="normal"
        color={subtleText}
        textTransform="uppercase"
        letterSpacing="wider"
        px={3}
        pt={1}
        pb={1.5}
      >
        {label}
      </Text>
    </MotionBox>
  );
  const [isDialectColumnMounted, setIsDialectColumnMounted] =
    useState(hasDialectPicker);

  useLayoutEffect(() => {
    if (hasDialectPicker) {
      setIsDialectColumnMounted(true);
    }
  }, [hasDialectPicker]);

  const shouldRenderDialectColumn = hasDialectPicker || isDialectColumnMounted;

  return (
    <Box
      as="section"
      data-testid="create-session-interpreter-section"
      data-has-top-divider={showTopDivider ? 'true' : 'false'}
      aria-labelledby="create-session-interpreter-heading"
      borderTopWidth={showTopDivider ? '1px' : 0}
      borderColor={borderColor}
      pt={showTopDivider ? 4 : 0}
    >
      <Flex
        gap={{base: 2, md: 3}}
        align={{base: 'stretch', md: 'flex-start'}}
        direction={{base: 'column', md: 'row'}}
      >
        <Text
          as="h3"
          id="create-session-interpreter-heading"
          color={textColor}
          {...CREATE_SESSION_SECTION_LABEL_PROPS}
        >
          Interpreter
        </Text>
        <Box
          flex="1"
          minW={0}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <Flex
            role="radiogroup"
            aria-labelledby="create-session-interpreter-heading"
            bg={trackBg}
            borderRadius="lg"
            p={1}
            gap={1}
            direction="row"
            w={{base: '100%', md: '168px'}}
            maxW="100%"
            flexShrink={0}
          >
            {INTERPRETER_OPTIONS.map(option => (
              <SegmentedRadioOption
                key={option.id}
                name="create-session-interpreter"
                value={option.id}
                label={option.label}
                isSelected={interpreterEnabled === option.enabled}
                onChange={() => handleInterpreterToggle(option.enabled)}
              />
            ))}
          </Flex>

          <CollapsibleContent
            isVisible={interpreterEnabled}
            mt={4}
            w="100%"
            data-testid="interpreter-fields"
          >
            {setSimultaneousEnabled && showSimultaneous ? (
              <FormControl
                display="flex"
                alignItems="flex-start"
                mb={4}
                data-testid="simultaneous-toggle"
              >
                <Switch
                  id="create-session-simultaneous"
                  isChecked={Boolean(simultaneousEnabled)}
                  onChange={event =>
                    setSimultaneousEnabled(event.target.checked)
                  }
                  mt={1}
                  mr={3}
                />
                <Box>
                  <FormLabel
                    htmlFor="create-session-simultaneous"
                    color={textColor}
                    fontWeight="normal"
                    fontSize="sm"
                    mb={0}
                  >
                    Simultaneous interpretation (beta)
                  </FormLabel>
                  <Text fontSize="xs" color={subtleText}>
                    Low-latency, continuous speech-to-speech translation. Each
                    side hears only their own language — when a speaker uses the
                    listener&apos;s language, that segment stays silent.
                  </Text>
                </Box>
              </FormControl>
            ) : null}
            <Box
              data-testid="language-dialect-row"
              display="grid"
              gridTemplateColumns={{
                base: 'minmax(0, 1fr)',
                md: hasDialectPicker
                  ? 'minmax(0, 1fr) minmax(0, 1fr)'
                  : 'minmax(0, 1fr) minmax(0, 0fr)',
              }}
              columnGap={{base: 0, md: hasDialectPicker ? 3 : 0}}
              rowGap={3}
              alignItems="flex-start"
              data-dialect-layout={hasDialectPicker ? 'split' : 'single'}
              data-motion-duration={`${LANGUAGE_DIALECT_MOVE_DURATION_MS}ms`}
              data-motion-ease={LANGUAGE_DIALECT_MOVE_EASE_CSS}
              transition={[
                `grid-template-columns ${LANGUAGE_DIALECT_MOVE_DURATION_MS}ms ${LANGUAGE_DIALECT_MOVE_EASE_CSS}`,
                `column-gap ${LANGUAGE_DIALECT_MOVE_DURATION_MS}ms ${LANGUAGE_DIALECT_MOVE_EASE_CSS}`,
              ].join(', ')}
            >
              <FormControl
                isInvalid={Boolean(visibleErrors.language)}
                w="100%"
                minW={0}
              >
                <FormLabel
                  color={textColor}
                  fontWeight="normal"
                  fontSize="sm"
                  whiteSpace="nowrap"
                >
                  Language
                </FormLabel>
                <Box
                  ref={languageTypeaheadRef}
                  position="relative"
                  onBlur={event => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setIsLanguageTypeaheadOpen(false);
                    }
                  }}
                >
                  <SearchSelectionInput
                    ariaControls="create-session-language-results"
                    ariaLabel="Language"
                    isOpen={isLanguageTypeaheadOpen}
                    needsAttention={Boolean(visibleErrors.language)}
                    onChange={handleLanguageSearchChange}
                    onClick={handleLanguageFocus}
                    onFocus={handleLanguageFocus}
                    onKeyDown={(event: any) =>
                      handleDropdownInputKeyDown({
                        event,
                        menuId: 'create-session-language-results',
                        onOpen: handleLanguageFocus,
                      })
                    }
                    placeholder="Select language"
                    value={languageSearch}
                  />

                  {language && (
                    <InputClearButton
                      label="Clear language"
                      onClick={handleClearLanguage}
                    />
                  )}

                  {isLanguageTypeaheadOpen && (
                    <ViewportAwareDropdownMenu
                      id="create-session-language-results"
                      className="menu"
                      position="absolute"
                      top="calc(100% + 6px)"
                      left={0}
                      right={0}
                      zIndex={6}
                      bg={surfaceBg}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      boxShadow="lg"
                      maxHeightPx={260}
                      role="listbox"
                      aria-label="Language results"
                      p={2}
                    >
                      {shouldShowLanguageSearchStatus && (
                        <Text color={subtleText} fontSize="sm" px={3} pb={2}>
                          {filteredLanguageOptions.length} result
                          {filteredLanguageOptions.length !== 1 ? 's' : ''}
                        </Text>
                      )}
                      {filteredLanguageOptions.length > 0 ? (
                        <LayoutGroup>
                          {autoDetectOption &&
                            renderAutoDetectOption(autoDetectOption)}
                          {favoriteLanguageOptions.length > 0 &&
                            renderLanguageGroupLabel(
                              'Favorites',
                              'language-group-favorites',
                            )}
                          {favoriteLanguageOptions.map(renderLanguageOption)}
                          {favoriteLanguageOptions.length > 0 &&
                            otherLanguageOptions.length > 0 &&
                            renderLanguageGroupLabel(
                              'All languages',
                              'language-group-all',
                            )}
                          {otherLanguageOptions.map(renderLanguageOption)}
                        </LayoutGroup>
                      ) : (
                        <Text color={subtleText} fontSize="sm" px={3} py={3}>
                          No language matches.
                        </Text>
                      )}
                    </ViewportAwareDropdownMenu>
                  )}
                </Box>
                <FormErrorMessage>{visibleErrors.language}</FormErrorMessage>
              </FormControl>

              {shouldRenderDialectColumn && (
                <Box
                  data-testid="dialect-field-shell"
                  data-dialect-visible={hasDialectPicker ? 'true' : 'false'}
                  minW={0}
                  w="100%"
                  overflow={hasDialectPicker ? 'visible' : 'hidden'}
                  pointerEvents={hasDialectPicker ? 'auto' : 'none'}
                  aria-hidden={hasDialectPicker ? undefined : true}
                >
                  <AnimatePresence
                    onExitComplete={() => {
                      if (!hasDialectPicker) {
                        setIsDialectColumnMounted(false);
                      }
                    }}
                  >
                    {hasDialectPicker && (
                      <MotionBox
                        key="dialect-picker"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={DIALECT_PICKER_FADE_TRANSITION}
                      >
                        <FormControl
                          isInvalid={Boolean(visibleErrors.dialect)}
                          minW={0}
                          w="100%"
                        >
                          <FormLabel
                            color={textColor}
                            fontWeight="normal"
                            fontSize="sm"
                            whiteSpace="nowrap"
                          >
                            Dialect
                          </FormLabel>
                          <Box
                            ref={dialectTypeaheadRef}
                            position="relative"
                            onBlur={event => {
                              if (
                                !event.currentTarget.contains(
                                  event.relatedTarget,
                                )
                              ) {
                                setIsDialectTypeaheadOpen(false);
                              }
                            }}
                          >
                            <SearchSelectionInput
                              ariaControls="create-session-dialect-results"
                              ariaLabel="Dialect"
                              isOpen={isDialectTypeaheadOpen}
                              onChange={handleDialectSearchChange}
                              onClick={() => setIsDialectTypeaheadOpen(true)}
                              onFocus={() => setIsDialectTypeaheadOpen(true)}
                              onKeyDown={(event: any) =>
                                handleDropdownInputKeyDown({
                                  event,
                                  menuId: 'create-session-dialect-results',
                                  onOpen: () => setIsDialectTypeaheadOpen(true),
                                })
                              }
                              placeholder="Select dialect"
                              value={dialectSearch}
                            />

                            {dialect && (
                              <InputClearButton
                                label="Clear dialect"
                                onClick={handleClearDialect}
                              />
                            )}

                            {isDialectTypeaheadOpen && (
                              <ViewportAwareDropdownMenu
                                id="create-session-dialect-results"
                                className="menu"
                                position="absolute"
                                top="calc(100% + 6px)"
                                left={0}
                                right={0}
                                zIndex={6}
                                bg={surfaceBg}
                                borderWidth="1px"
                                borderColor={borderColor}
                                borderRadius="lg"
                                boxShadow="lg"
                                maxHeightPx={220}
                                role="listbox"
                                aria-label="Dialect results"
                                p={2}
                              >
                                {filteredDialectOptions.length > 0 ? (
                                  filteredDialectOptions.map((option: any) => (
                                    <Button
                                      key={option.id}
                                      type="button"
                                      className="item"
                                      role="option"
                                      aria-selected={dialect === option.dialect}
                                      aria-label={`Select ${option.label}`}
                                      variant="ghost"
                                      justifyContent="space-between"
                                      w="100%"
                                      h="auto"
                                      minH="44px"
                                      py={2.5}
                                      px={3}
                                      borderRadius="lg"
                                      bg={
                                        dialect === option.dialect
                                          ? typeaheadSelectedBg
                                          : 'transparent'
                                      }
                                      borderWidth="1px"
                                      borderColor={
                                        dialect === option.dialect
                                          ? typeaheadSelectedBorder
                                          : 'transparent'
                                      }
                                      // See the language option above: keep focus on the
                                      // input so Safari does not blur-close the menu before
                                      // the click selects this dialect.
                                      onMouseDown={event =>
                                        event.preventDefault()
                                      }
                                      onClick={() =>
                                        handleSelectDialectOption(option)
                                      }
                                      onKeyDown={event =>
                                        handleDropdownOptionKeyDown(
                                          event,
                                          'input[aria-label="Dialect"]',
                                        )
                                      }
                                      _hover={{
                                        bg:
                                          dialect === option.dialect
                                            ? typeaheadSelectedBg
                                            : typeaheadHoverBg,
                                      }}
                                      _focus={{
                                        bg:
                                          dialect === option.dialect
                                            ? typeaheadSelectedBg
                                            : typeaheadHoverBg,
                                        borderColor:
                                          dialect === option.dialect
                                            ? typeaheadSelectedBorder
                                            : 'transparent',
                                        boxShadow: 'none',
                                        outline: 'none',
                                      }}
                                      _focusVisible={{
                                        bg:
                                          dialect === option.dialect
                                            ? typeaheadSelectedBg
                                            : typeaheadHoverBg,
                                        borderColor:
                                          dialect === option.dialect
                                            ? typeaheadSelectedBorder
                                            : 'transparent',
                                        boxShadow: 'none',
                                        outline: 'none',
                                      }}
                                    >
                                      <Text
                                        color={textColor}
                                        fontWeight="normal"
                                        fontSize="md"
                                        noOfLines={1}
                                      >
                                        {option.label}
                                      </Text>
                                      {option.isCommon && (
                                        // Same neutral pill the planner's
                                        // recent rows render (MetaPill with
                                        // the squarer app-wide radius).
                                        <MetaPill
                                          borderRadius="md"
                                          fontSize="sm"
                                          flexShrink={0}
                                          ml={2}
                                        >
                                          Common
                                        </MetaPill>
                                      )}
                                    </Button>
                                  ))
                                ) : (
                                  <Text
                                    color={subtleText}
                                    fontSize="sm"
                                    px={3}
                                    py={3}
                                  >
                                    No dialect matches.
                                  </Text>
                                )}
                              </ViewportAwareDropdownMenu>
                            )}
                          </Box>
                          <FormErrorMessage>
                            {visibleErrors.dialect}
                          </FormErrorMessage>
                        </FormControl>
                      </MotionBox>
                    )}
                  </AnimatePresence>
                </Box>
              )}
            </Box>
          </CollapsibleContent>
        </Box>
      </Flex>
    </Box>
  );
};

export const LocationSection = ({
  alreadyWithPatient,
  handleLocationChange,
  interpreterEnabled,
  locationType,
  patientPhone,
  phoneJoinMode,
  providerPhone,
  callerId,
  setCallerId,
  setAlreadyWithPatient,
  setPatientPhone,
  setPhoneJoinMode,
  setProviderPhone,
  setVideoCallLink,
  visibleErrors,
  videoCallLink,
}: Record<string, any>) => {
  const {borderColor, inputFocusStyles, subtleText, textColor, trackBg} =
    usePlannerStyles();
  const manualCall = useDisclosure();

  // Web dial-out: patient not on the line and the provider joins on this
  // device. Hides the provider phone (they use the browser) and keeps the
  // patient phone (we SIP-dial them into the web room).
  const isWebDialOut = !alreadyWithPatient && phoneJoinMode !== 'phone';
  const showProviderPhone = !isWebDialOut;
  const showPatientPhone = !alreadyWithPatient;
  // Who the outbound caller ID rings/shows: web dial-out rings only the
  // patient; the scribe and already-on-the-line flows ring only the provider
  // (they merge the patient themselves); the interpreter's phone three-way
  // rings both. Provider-facing dials default to the org's verified number so
  // the call bypasses the clinic phone system's queue.
  const callerIdDialTarget = isWebDialOut
    ? 'patient'
    : alreadyWithPatient || !interpreterEnabled
      ? 'provider'
      : 'both';
  const phoneHelperText = alreadyWithPatient
    ? 'Opalite will call your number. Answer, then merge your patient into the call.'
    : isWebDialOut
      ? interpreterEnabled
        ? "We'll start the session on this device and call the patient's phone — talk through your computer while Opalite interprets live."
        : "We'll start the session on this device and call the patient's phone — stay here to follow the live transcript."
      : interpreterEnabled
        ? 'Opalite calls you first, then dials your patient and merges the call automatically.'
        : 'Opalite will call your number. Add your patient to the call after you answer — Opalite transcribes the conversation.';

  // Switching "on this device" ↔ "on my phone" (and the already-with-patient
  // toggle) swaps which numbers and helper copy show. Keying the body on that
  // configuration cross-fades it on the shared curve instead of hard-cutting.
  const phoneFieldsSignature = [
    showProviderPhone,
    showPatientPhone,
    alreadyWithPatient,
    isWebDialOut,
    interpreterEnabled,
  ].join('-');

  return (
    <Box
      as="section"
      aria-labelledby="create-session-location-heading"
      borderTopWidth="1px"
      borderColor={borderColor}
      pt={4}
    >
      <Flex
        gap={{base: 2, md: 3}}
        align={{base: 'stretch', md: 'flex-start'}}
        direction={{base: 'column', md: 'row'}}
      >
        <Text
          as="h3"
          id="create-session-location-heading"
          color={textColor}
          {...CREATE_SESSION_SECTION_LABEL_PROPS}
        >
          Location
        </Text>
        <Box flex="1" minW={0}>
          <Flex
            role="radiogroup"
            aria-labelledby="create-session-location-heading"
            bg={trackBg}
            borderRadius="lg"
            p={1}
            gap={1}
            direction={{base: 'column', sm: 'row'}}
          >
            {LOCATION_OPTIONS.map(option => (
              <SegmentedRadioOption
                key={option.id}
                name="create-session-location"
                value={option.id}
                label={option.label}
                icon={option.icon}
                isSelected={locationType === option.id}
                onChange={() => handleLocationChange(option.id)}
              />
            ))}
          </Flex>

          <CollapsibleContent
            isVisible={locationType !== 'inPerson'}
            mt={4}
            contentKey={locationType}
          >
            {locationType === 'phone' && (
              <Box data-testid="phone-location-fields">
                {/* Equal desktop columns keep the two phone toggles aligned
                    with the split form rows above and below. */}
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    base: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                  }}
                  columnGap={{base: 0, md: 3}}
                  rowGap={3}
                  alignItems="start"
                  mb={3}
                >
                  <Box w="100%" textAlign="left">
                    <Text
                      as="span"
                      display="block"
                      color={textColor}
                      fontWeight="normal"
                      fontSize="sm"
                      mb={1}
                    >
                      Already on phone with patient?
                    </Text>
                    <Flex
                      role="radiogroup"
                      aria-label="Already on phone with patient?"
                      bg={trackBg}
                      borderRadius="lg"
                      p={1}
                      gap={1}
                      w={{base: '100%', md: '168px'}}
                      maxW="100%"
                    >
                      <SegmentedRadioOption
                        name="create-session-already-with-patient"
                        value="no"
                        label="No"
                        ariaLabel="Not with the patient yet"
                        isSelected={!alreadyWithPatient}
                        onChange={() => setAlreadyWithPatient(false)}
                      />
                      <SegmentedRadioOption
                        name="create-session-already-with-patient"
                        value="yes"
                        label="Yes"
                        ariaLabel="Already with the patient"
                        isSelected={Boolean(alreadyWithPatient)}
                        onChange={() => setAlreadyWithPatient(true)}
                      />
                    </Flex>
                  </Box>

                  {!alreadyWithPatient && (
                    <Box
                      data-testid="phone-join-mode"
                      w="100%"
                      textAlign="left"
                    >
                      <Text
                        as="span"
                        display="block"
                        color={textColor}
                        fontWeight="normal"
                        fontSize="sm"
                        mb={1}
                      >
                        Where will you be?
                      </Text>
                      <Flex
                        role="radiogroup"
                        aria-label="Where will you be?"
                        bg={trackBg}
                        borderRadius="lg"
                        p={1}
                        gap={1}
                        w="fit-content"
                        maxW="100%"
                      >
                        <SegmentedRadioOption
                          name="create-session-phone-join-mode"
                          value="web"
                          label="This device"
                          ariaLabel="Join on this device"
                          isSelected={phoneJoinMode !== 'phone'}
                          onChange={() => setPhoneJoinMode('web')}
                          noTruncate
                        />
                        <SegmentedRadioOption
                          name="create-session-phone-join-mode"
                          value="phone"
                          label="My phone"
                          ariaLabel="Join on my phone"
                          isSelected={phoneJoinMode === 'phone'}
                          onChange={() => setPhoneJoinMode('phone')}
                          noTruncate
                        />
                      </Flex>
                    </Box>
                  )}
                </Box>

                <MotionBox
                  key={phoneFieldsSignature}
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  transition={FORM_FADE_TRANSITION}
                >
                  <Box
                    data-testid="phone-number-fields"
                    data-phone-number-layout={
                      showPatientPhone && showProviderPhone ? 'split' : 'single'
                    }
                    display="grid"
                    gridTemplateColumns={{
                      base: '1fr',
                      md:
                        showPatientPhone && showProviderPhone
                          ? 'repeat(2, minmax(0, 1fr))'
                          : '1fr',
                    }}
                    gap={3}
                    minH={{md: '72px'}}
                  >
                    {showPatientPhone && (
                      <FormControl
                        isInvalid={Boolean(visibleErrors.patientPhone)}
                      >
                        <FormLabel
                          color={textColor}
                          fontWeight="normal"
                          fontSize="sm"
                        >
                          Patient Number
                        </FormLabel>
                        <HStack spacing={2} align="flex-start">
                          <Input
                            flex="1"
                            value={splitPhoneExtension(patientPhone).number}
                            onChange={event =>
                              setPatientPhone(
                                combinePhoneExtension(
                                  formatCreateSessionPhoneNumber(
                                    event.target.value,
                                  ),
                                  splitPhoneExtension(patientPhone).extension,
                                ),
                              )
                            }
                            placeholder="Patient phone"
                            aria-label="Patient Number"
                            type="tel"
                            borderRadius="lg"
                            borderColor={borderColor}
                            _hover={{borderColor}}
                            _focusVisible={inputFocusStyles}
                          />
                          <Input
                            w="84px"
                            flexShrink={0}
                            value={splitPhoneExtension(patientPhone).extension}
                            onChange={event =>
                              setPatientPhone(
                                combinePhoneExtension(
                                  splitPhoneExtension(patientPhone).number,
                                  event.target.value,
                                ),
                              )
                            }
                            placeholder="Ext."
                            aria-label="Patient Extension"
                            type="tel"
                            inputMode="numeric"
                            borderRadius="lg"
                            borderColor={borderColor}
                            _hover={{borderColor}}
                            _focusVisible={inputFocusStyles}
                          />
                        </HStack>
                        <FormErrorMessage>
                          {visibleErrors.patientPhone}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                    {showProviderPhone && (
                      <FormControl
                        isInvalid={Boolean(visibleErrors.providerPhone)}
                      >
                        <FormLabel
                          color={textColor}
                          fontWeight="normal"
                          fontSize="sm"
                        >
                          Your Number
                        </FormLabel>
                        <HStack spacing={2} align="flex-start">
                          <Input
                            flex="1"
                            value={splitPhoneExtension(providerPhone).number}
                            onChange={event =>
                              setProviderPhone(
                                combinePhoneExtension(
                                  formatCreateSessionPhoneNumber(
                                    event.target.value,
                                  ),
                                  splitPhoneExtension(providerPhone).extension,
                                ),
                              )
                            }
                            placeholder="Your phone"
                            aria-label="Provider Number"
                            type="tel"
                            borderRadius="lg"
                            borderColor={borderColor}
                            _hover={{borderColor}}
                            _focusVisible={inputFocusStyles}
                          />
                          <Input
                            w="84px"
                            flexShrink={0}
                            value={splitPhoneExtension(providerPhone).extension}
                            onChange={event =>
                              setProviderPhone(
                                combinePhoneExtension(
                                  splitPhoneExtension(providerPhone).number,
                                  event.target.value,
                                ),
                              )
                            }
                            placeholder="Ext."
                            aria-label="Provider Extension"
                            type="tel"
                            inputMode="numeric"
                            borderRadius="lg"
                            borderColor={borderColor}
                            _hover={{borderColor}}
                            _focusVisible={inputFocusStyles}
                          />
                        </HStack>
                        <FormErrorMessage>
                          {visibleErrors.providerPhone}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Box>

                  <Text fontSize="xs" color={subtleText} mt={3}>
                    {phoneHelperText}
                  </Text>
                </MotionBox>

                {/* Outside the cross-fading MotionBox: the field applies to
                    every phone configuration, and staying mounted across
                    toggle flips preserves its has-the-user-picked tracking
                    (which drives the verified-number default). */}
                <CallerIdField
                  value={callerId || ''}
                  setCallerId={setCallerId}
                  dialTarget={callerIdDialTarget}
                />

                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  mt={3}
                  leftIcon={<TablerPhone size={16} aria-hidden="true" />}
                  onClick={manualCall.onOpen}
                  whiteSpace="nowrap"
                >
                  Want to call manually?
                </Button>

                <ManualCallModal
                  isOpen={manualCall.isOpen}
                  onClose={manualCall.onClose}
                />
              </Box>
            )}

            {locationType === 'video' && (
              <Box data-testid="video-location-fields" minH={{md: '72px'}}>
                <FormControl isInvalid={Boolean(visibleErrors.videoCallLink)}>
                  <FormLabel
                    color={textColor}
                    fontWeight="normal"
                    fontSize="sm"
                    mb={1}
                  >
                    Video Call Link
                  </FormLabel>
                  <Text fontSize="xs" color={subtleText} mb={2}>
                    Supports Zoom, Microsoft Teams, Google Meet, RingCentral,
                    and WebEx.
                  </Text>
                  <Input
                    value={videoCallLink}
                    onChange={event => setVideoCallLink(event.target.value)}
                    placeholder="Paste your meeting link"
                    aria-label="Video Call Link"
                    type="url"
                    borderRadius="lg"
                    borderColor={borderColor}
                    _hover={{borderColor}}
                    _focusVisible={inputFocusStyles}
                  />
                  <FormErrorMessage>
                    {visibleErrors.videoCallLink}
                  </FormErrorMessage>
                </FormControl>
              </Box>
            )}
          </CollapsibleContent>
        </Box>
      </Flex>
    </Box>
  );
};

export const CreateSessionActionBar = ({
  children,
  ...rest
}: Record<string, any>) => {
  const {borderColor} = usePlannerStyles();
  return (
    <HStack
      borderTopWidth="1px"
      borderColor={borderColor}
      px={CREATE_SESSION_CARD_PADDING}
      py={4}
      spacing={3}
      justify="flex-end"
      flexWrap="wrap"
      {...rest}
    >
      {children}
    </HStack>
  );
};

export const CreateSessionFooter = ({
  canSchedule = false,
  canStart = false,
  isStarting = false,
  onSchedule,
  onStart,
}: Record<string, any>) => (
  <CreateSessionActionBar>
    {canSchedule && (
      <Button
        type="button"
        variant="outline"
        leftIcon={<TablerCalendar size={16} />}
        onClick={onSchedule}
      >
        Schedule
      </Button>
    )}
    <Button
      type="button"
      variant="opalitePrimary"
      leftIcon={<TablerPlayerPlay size={16} />}
      onClick={onStart}
      px={6}
      isLoading={isStarting}
      loadingText="Starting"
      aria-disabled={!canStart}
      opacity={canStart ? 1 : 0.55}
      _hover={canStart ? undefined : {opacity: 0.7}}
    >
      Start
    </Button>
  </CreateSessionActionBar>
);
